<script setup lang="ts">
import {
  onMounted, type Ref, ref,
} from 'vue';
import {
  CdrText, CdrButton,
} from '@rei/cedar';
import { camelCase } from '../../common/utils';
import { sendClickAnalytics } from '../../utils';
import tags from '../../utils/globalTags';
import { useDataParsing } from '../../common/composables/useArrayParsing';
import type { QuickToolsModel } from '../../api/types/QuickToolsModel';
import { apiCall } from '../../api/services/apiCalls';

const { from, adobeTag } = defineProps<{
  from?: boolean,
  adobeTag?: string,
}>();

const quickToolsArray: Ref<QuickToolsModel[]> = ref([]);

const quickToolsHandler = (tool: QuickToolsModel) => {
  const adobeMainTag = from
    ? `${(adobeTag?.replace(/([^\w]+|\s+)/g, '_'))}_Tools`
    : 'Homepage_Tools';
  const clickData = {
    pageName: null,
    linkName: `${tags.HELP_CENTER}_${adobeMainTag}_${camelCase(tool.title)}`,
  };
  sendClickAnalytics(clickData);
  window.open(tool.url, '_self');
};

onMounted(async () => {
  const query = window !== undefined && new URL(window.location.href).searchParams.get('hc');
  quickToolsArray.value = await apiCall('default', 'quick-tools', query).then((res) => useDataParsing('threeWaysArray', res));
});
</script>

<template>
  <div class="quick-tools__wrap">
    <CdrText
      tag="h2"
      tabindex="0"
      class="help-center__heading-links"
    >
      Quick tools
    </CdrText>
    <div
      id="quick-tools"
      class="quick-tools"
      :class="from && 'quick-tools-from-category'"
    >
      <div class="quick-tools-row">
        <div
          v-for="(tool, i) in quickToolsArray.flat(1)"
          :key="i"
          class="quick-tools-column"
        >
          <div
            :key="`${tool?.title}-${i}`"
            :class="from && 'quick-tools-from-category-column'"
            :data-ui="tool?.title"
          >
            <div
              tabindex="0"
              class="quick-tools-section-wrapper"
            >
              <div
                class="quick-tools-sectionValues"
                :class="from && 'quick-tools-from-category-sectionValues'"
              >
                <div class="quick-tools-secVal">
                  <div class="quick-tools-secVal-it">
                    <div
                      role="heading"
                      aria-level="2"
                      class="quick-tools-sec-heading"
                    >
                      {{ tool?.title }}
                    </div>
                  </div>
                </div>
                <CdrText
                  class="quick-tools-sec-body"
                  :style="{'zoom':from ? '.97' : null}"
                >
                  {{ tool?.description }}
                </CdrText>
                <div class="quick-tools-sec-button">
                  <cdr-button
                    modifier="secondary"
                    @click="quickToolsHandler(tool)"
                  >
                    {{ tool?.buttonTitle }}
                  </cdr-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import 'src/main/style/quickTools.scss';
</style>
